import { useState } from "react";
import { useTranslation } from "react-i18next";
import { chosenProjectDecision, useProjects } from "../../../projects/projectContext";
import BlockedState from "../../../ui/feedback/BlockedState";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";
import CloudImportForm from "./CloudImportForm";
import LocalImportForm from "./LocalImportForm";
import type { ImportFormProps } from "./importSetup";

type ImportKind = "local" | "cloud";

/**
 * The one way in for documents (F4, F16), as the import's input view in the Thread view's
 * preparation (web-app.md WA-12): Lokal (a file or folder from this PC) or Cloud (a Git
 * repository). Lokal needs `propose_change`; Cloud connects a source and so needs
 * `manage_curators`, as the server does.
 */
export default function ImportForms({ projectId, corpusId, onStarted }: ImportFormProps) {
  const { t } = useTranslation();
  const projects = useProjects();
  const [kind, setKind] = useState<ImportKind>("local");

  // The fields read the chosen project's corpora and permissions: they import only into the import's own project.
  if (projects.selectedProjectId !== projectId) {
    return <StatusMessage tone="info">{t("sources.import.otherProject")}</StatusMessage>;
  }
  return (
    <BlockedState decision={chosenProjectDecision(projects, "propose_change")}>
      <div className="space-y-5">
        <p className="app-text-muted text-sm">{t("sources.import.intro")}</p>
        <SegmentedControl<ImportKind>
          label={t("sources.import.kind")}
          size="regular"
          options={[
            { key: "local", label: t("sources.import.kinds.local"), title: t("sources.import.kindHints.local") },
            { key: "cloud", label: t("sources.import.kinds.cloud"), title: t("sources.import.kindHints.cloud"), requires: chosenProjectDecision(projects, "manage_curators") },
          ]}
          selected={kind}
          onSelect={setKind}
        />
        {kind === "local" ? (
          <LocalImportForm key={projectId} projectId={projectId} corpusId={corpusId} onStarted={onStarted} />
        ) : (
          <CloudImportForm key={projectId} projectId={projectId} corpusId={corpusId} onStarted={onStarted} />
        )}
      </div>
    </BlockedState>
  );
}
